import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Item from "../../components/Dashboard/Item";
import BudgetCard from "../../components/Dashboard/BudgetCard";
import Notification from "../../components/Notification";
import { Auth } from "aws-amplify";
import Projects from "../../components/Project/Projects";
import SellerProfilesCard from "../../components/Dashboard/SellerProfilesCard";
import ActivityNotifications from "../../components/Dashboard/ActivityNotifications";
import { useCompanyData } from "../../hooks/useCompanyData";
import InviteMemberModal from "../../components/User/InviteMemberModal";
import { buyerSubRoles } from "../../config/ROLES";
const { ApiHelper } = require("../../utils/ApiHelper");
const { BUYER_DASHBOARD_STATS, BUYER_MILESTONE_STATS, UPDATE_COMPANY } = require("../../lib/API_KEYS");

export default function Dashboard() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const { company: buyerData, loading: companyLoading } = useCompanyData();
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState({
    totalRequirements: 0,
    inDiscussion: 0,
    inExecution: 0,
    complete: 0
  });
  const [acceptedDismissed, setAcceptedDismissed] = useState(false);
  const [showInviteMemberModal, setShowInviteMemberModal] = useState(false);
  const [milestoneStats, setMilestoneStats] = useState({
    paid: { count: 0, amount: 0 },
    upcoming: { count: 0, amount: 0 },
    completed: { count: 0, amount: 0 },
    total: { count: 0, amount: 0 }
  });

  const handleDismissCongratulations = async () => {
    try {
      await new ApiHelper().FetchFromServer(
        UPDATE_COMPANY[0].replace(":companyId", buyerData?._id),
        UPDATE_COMPANY[1],
        undefined, { closeMessage: true })
      setAcceptedDismissed(true);
    } catch (err) {
      console.error('Error dismissing congratulations:', err);
    }
  };

  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const fullName = user.attributes.name;
        const first = fullName.split(" ")[0];
        setUsername(first);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };

    fetchUser();
  }, []);


  useEffect(() => {
    if (buyerData) {
      setLoading(false);
    }
  }, [buyerData]);

  const fetchMilestoneStats = async () => {
    try {
      const res = await new ApiHelper().FetchFromServer(
        ...BUYER_MILESTONE_STATS,
      );
      setMilestoneStats(res.data || {
        paid: { count: 0, amount: 0 },
        upcoming: { count: 0, amount: 0 },
        completed: { count: 0, amount: 0 },
        total: { count: 0, amount: 0 }
      });
    } catch (err) {
      console.error("Error fetching milestone stats:", err);
    }
  };
  
  const fetchDashboardStats = async () => {
    try {
      const res = await new ApiHelper().FetchFromServer(
        ...BUYER_DASHBOARD_STATS,
      );
      setDashboardStats(res.data || {
        totalRequirements: 0,
        inDiscussion: 0,
        inExecution: 0,
        complete: 0
      });
    } catch (err) {
      console.error("Error fetching dashboard stats:", err);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchMilestoneStats();
  }, []);

  const renderNotification = () => {
    if (loading) return null;
    // if (acceptedDismissed) return null;

    if (!buyerData?.status || buyerData?.status === 'Pending') {
      return (
        <Notification
          variant="empty"
          heading="You need to fill out company information form in order to be reviewed"
          subHeading="The applications not filled out within 24 hours will be deleted automatically"
          link="/buyer-dashboard/additional-info"
          text="Fill out Form"
        />
      );
    }

    switch (buyerData.status) {
      case "In Review":
        return (
          <Notification
            variant="pending"
            heading="Your application is under consideration"
            subHeading="Your application will be reviewed within 24 hours. Once admin approves or rejects, we'll notify you."
          />
        );

      case "Query Raised":
        return (
          <Notification
            variant="rejected"
            heading="Admin Raised Concerns About Your Application"
            subHeading={buyerData.concern || "Please review and update your application information."}
            link="/buyer-dashboard/additional-info"
            text="Update Application Info"
            buttonStyle={{ backgroundColor: "#ffc107", color: "white" }}
          />
        );

      case "Rejected":
        return (
          <Notification
            variant="rejected"
            heading="Your application was permanently rejected"
            subHeading={buyerData.rejectionReason || "Unfortunately your application was rejected. Please contact support if you believe this was an error."}
          />
        );

      case "Approved":
        return (
          (!buyerData?.closeMessage && !acceptedDismissed) && <Notification
            variant="accepted"
            heading="Congratulations! You have been reviewed and approved"
            subHeading="Now you can use our platform as a verified user"
            onClose={handleDismissCongratulations}
          />
        );

      default:
        return null;
    }
  };


  const handleMetricClick = (metricType) => {
    switch (metricType) {
      case 'total':
        // Navigate to requirements page - all requirements
        navigate('/buyer-dashboard/requirements');
        break;
      case 'discussion':
        // Navigate to requirements page with filter for discussion statuses
        navigate('/buyer-dashboard/requirements', {
          state: {
            filterStatuses: ['Seller Intimation']
          }
        });
        break;
      case 'execution':
        // Navigate to projects page with filter for execution statuses
        navigate('/buyer-dashboard/projects', {
          state: {
            filterStatuses: ['Tripartite Agreement Signed', 'Project Manager Assigned', 'Milestones Added', 'Milestones Completed']
          }
        });
        break;
      case 'complete':
        // Navigate to projects page with filter for completed status
        navigate('/buyer-dashboard/projects', {
          state: {
            filterStatuses: ['Feedback Completed']
          }
        });
        break;
      default:
        break;
    }
  };

  const items = [
    {
      icon: "/images/total.svg",
      number: dashboardStats.totalRequirements,
      subtitle: "Total Requirements",
      onClick: () => handleMetricClick('total')
    },
    {
      icon: "/images/review.svg",
      number: dashboardStats.inDiscussion,
      subtitle: "In Discussion",
      onClick: () => handleMetricClick('discussion')
    },
    {
      icon: "/images/fileUpload.svg",
      number: dashboardStats.inExecution,
      subtitle: "In Execution",
      onClick: () => handleMetricClick('execution')
    },
    {
      icon: "/images/completed.svg",
      number: dashboardStats.complete,
      subtitle: "Complete",
      onClick: () => handleMetricClick('complete')
    },
  ];

  const budgetData = {
    title: "Milestone Overview",
    totalSales: `$${milestoneStats.total.amount.toLocaleString()}`,
    growth: milestoneStats.total.count > 0 ? `+${milestoneStats.total.count} milestones` : "No milestones",
    bars: [
      { width: milestoneStats.total.amount > 0 ? `${(milestoneStats.paid.amount / milestoneStats.total.amount) * 100}%` : "0%", color: "#1665D8" },
      { width: milestoneStats.total.amount > 0 ? `${(milestoneStats.upcoming.amount / milestoneStats.total.amount) * 100}%` : "0%", color: "#DC3545" },
      { width: milestoneStats.total.amount > 0 ? `${(milestoneStats.completed.amount / milestoneStats.total.amount) * 100}%` : "0%", color: "#2AB885" },
    ],
    categories: [
      { name: "Paid", color: "#1665D8" },
      { name: "Upcoming", color: "#DC3545" },
      { name: "Completed", color: "#2AB885" },
    ],
    stats: [
      {
        name: "Paid",
        amount: `$${milestoneStats.paid.amount.toLocaleString()}`,
        change: `${milestoneStats.paid.count} milestones`,
        positive: true
      },
      {
        name: "Upcoming",
        amount: `$${milestoneStats.upcoming.amount.toLocaleString()}`,
        change: `${milestoneStats.upcoming.count} milestones`,
        positive: false
      },
      {
        name: "Completed",
        amount: `$${milestoneStats.completed.amount.toLocaleString()}`,
        change: `${milestoneStats.completed.count} milestones`,
        positive: true
      },
    ],
  };

  return (
    <>
      {renderNotification()}

      <div className="container my-4">
        <div className="row align-items-center justify-content-between">
          <div className="col-6 d-flex align-items-center">
            <img
              src="/images/hand.svg"
              alt="wave"
              className="me-2"
              style={{ width: "24px", height: "24px" }}
            />
            <h4 className="mb-0" style={{ maxWidth: "100%" }}>
              Hello, {username}
            </h4>
          </div>

          <div className="col-6 d-flex justify-content-end">
            <div
              className="d-inline-flex align-items-center px-3 py-2 text-nowrap flex-shrink-0"
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "50px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <img
                src="/images/calendar.svg"
                alt="Calendar"
                style={{ width: "24px", height: "24px", marginRight: "8px" }}
              />
              <span>{formattedDate}</span>
            </div>
          </div>

          <div className="col-12 mt-2">
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Your personalized dashboard for smarter control and seamless
              experience
            </p>
          </div>
        </div>
      </div>

      <div className="container-fluid px-4 mt-3">
        <div className="row align-items-stretch" style={{ minHeight: 320 }}>
          <div className="col-lg-4 mb-4 d-flex">
            <div className="row g-3 w-100 h-100">
              {items.map((item, index) => (
                <div className="col-6 d-flex" key={index}>
                  <Item
                    icon={item.icon}
                    number={item.number}
                    subtitle={item.subtitle}
                    onClick={item.onClick}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="col-lg-8 d-flex">
            <div className="border border-1 shadow-sm rounded-4 w-100 h-100 d-flex flex-column justify-content-between">
              <div className="d-flex flex-wrap align-items-center px-4 justify-content-between">
                <div
                  className="d-flex flex-column justify-content-between"
                  style={{ width: "55%" }}
                >
                  <div className="d-flex mb-3 mt-3">
                    <img src="/images/avatar1.svg" alt="Avatar 1" className="rounded-circle border" style={{ width: 40, height: 40, marginRight: -10, zIndex: 4 }} />
                    <img src="/images/avatar2.svg" alt="Avatar 2" className="rounded-circle border" style={{ width: 40, height: 40, marginRight: -10, zIndex: 3 }} />
                    <img src="/images/avatar3.svg" alt="Avatar 3" className="rounded-circle border" style={{ width: 40, height: 40, marginRight: -10, zIndex: 2 }} />
                    <img src="/images/avatar4.svg" alt="Avatar 4" className="rounded-circle border" style={{ width: 40, height: 40, zIndex: 1 }} />
                  </div>
                  <h5 className="fw-semibold mb-2">Invite your team member & start collaborating</h5>
                  <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                    Work together with your team by inviting members to your workspace. Collaborate, manage tasks, and share files easily.
                  </p>
                </div>
                <div style={{ width: "40%" }}>
                  <img src="/images/dashboardRightSide.svg" className="img-fluid" alt="sideimage" />
                </div>
              </div>
              <div className="text-center py-2 mb-1 border-top">
                <span className="fw-semibold" style={{ textDecoration: "underline dotted", cursor: "pointer" }} onClick={() => setShowInviteMemberModal(true)}>
                  Invite New Team Member
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="row align-items-stretch mt-4" style={{ minHeight: 320 }}>
          <div className="col-lg-4 mb-4 d-flex">
            <div className="w-100 h-100 border border-1 rounded-4 shadow-sm d-flex flex-column">
              <BudgetCard {...budgetData} />
            </div>
          </div>
          <div className="col-lg-8 d-flex">
            <SellerProfilesCard />
          </div>
        </div>
        <ActivityNotifications userRole="buyer" />
        <div id="projects-section">
          <Projects userRole={"Buyer"} />
        </div>
        <InviteMemberModal
          isOpen={showInviteMemberModal}
          onClose={() => setShowInviteMemberModal(false)}
          subRoles={buyerSubRoles}
        />
      </div>
    </>
  );
}
